/**
 * DUNE: подарунковий сертифікат — поведінка сцени й конфігуратора.
 * Працює у парі зі snippets/dune-gift.liquid і assets/dune-gift.css.
 *
 * Що робить:
 *   1. Тримає картку на сцені у згоді з обраним варіантом: сума крутиться
 *      одометром, бейдж і підписи на направляючих міняються, конверт зникає
 *      для електронного формату; направляючі стають по коробці картки.
 *   2. Веде повзунок рейки номіналів і жовту лінію рівня.
 *   3. Міняє рядок-пояснення під вибором формату; на пластику ховає поля
 *      отримувача і показує рядок про доставку.
 *   4. Перемикач «це подарунок» розкриває поля; ім'я з поля пишеться на
 *      картку, привітання — на конверт.
 *   5. Нахиляє об'єкт за курсором (тільки миша, не на reduced-motion).
 *   6. Прив'язує поля отримувача до форми товару (атрибут form=) і не пускає
 *      в кошик, доки не вказано ім'я і хоча б один контакт; порожні поля
 *      вимикає перед відправкою.
 *
 * Варіантами керує штатний скрипт теми (ProductForm в assets/theme.js): наші
 * радіо названі options[...] і лежать у секції, тема сама слухає їх change,
 * ставить id варіанта у форму й переписує ціну на кнопці. Суму для сцени
 * беремо самі з JSON товару (#ProductJson-<section id>) — так не чекаємо
 * подій теми, які вона шле лише з увімкненим useCustomEvents.
 *
 * Договір із чекаутом FLUX (ODDO/docs/tz-gift-cards.md): імена полів
 * properties[_gift_recipient_name|_phone|_email] і properties[_gift_message]
 * задає Liquid, цей скрипт їх не міняє.
 */
(function () {
  'use strict';

  var THIN = ' ';
  var REDUCED = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /** 20000 -> "20 000" (тонкий пробіл, як у snippets/dune-money.liquid). */
  function groupThousands(value) {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, THIN);
  }

  function digitsOf(text) {
    return parseInt(String(text || '').replace(/\D/g, ''), 10);
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  /* ------------------------------------------------------------------- */
  /* Одометр                                                             */
  /* ------------------------------------------------------------------- */

  function Odometer(el) {
    this.el = el;
    this.chars = [];
    this.value = null;

    var initial = digitsOf(el.textContent);
    el.textContent = '';
    this.set(isNaN(initial) ? 0 : initial, true);
  }

  Odometer.prototype.makeDigit = function (digit) {
    var slot = document.createElement('span');
    slot.className = 'dune-gift__odo-slot';

    var strip = document.createElement('span');
    strip.className = 'dune-gift__odo-strip';

    for (var i = 0; i < 10; i++) {
      var item = document.createElement('i');
      item.textContent = String(i);
      strip.appendChild(item);
    }

    strip.style.setProperty('--d', digit);
    slot.appendChild(strip);

    return { type: 'digit', el: slot, strip: strip, value: digit };
  };

  Odometer.prototype.makeSep = function () {
    var el = document.createElement('span');
    el.className = 'dune-gift__odo-sep';
    el.textContent = THIN;
    return { type: 'sep', el: el };
  };

  Odometer.prototype.rebuild = function (value) {
    this.el.textContent = '';
    this.chars = [];
    this.set(value, true);
  };

  Odometer.prototype.set = function (value, immediate) {
    var target = groupThousands(value).split('');
    var current = this.chars;
    var diff = target.length - current.length;
    var el = this.el;
    var added = [];
    var i;

    if (diff > 0) {
      for (i = diff - 1; i >= 0; i--) {
        var ch = target[i];
        var entry = ch === THIN ? this.makeSep() : this.makeDigit(parseInt(ch, 10));
        if (!immediate) entry.el.classList.add('is-new');
        el.insertBefore(entry.el, el.firstChild);
        current.unshift(entry);
        added.push(entry);
      }

      if (!immediate) {
        void el.offsetWidth;
        added.forEach(function (e) { e.el.classList.remove('is-new'); });
      }
    } else if (diff < 0) {
      var extra = current.splice(0, -diff);
      extra.forEach(function (e) {
        if (immediate) {
          if (e.el.parentNode) e.el.parentNode.removeChild(e.el);
          return;
        }
        e.el.classList.add('is-gone');
        setTimeout(function () {
          if (e.el.parentNode) e.el.parentNode.removeChild(e.el);
        }, 600);
      });
    }

    for (i = 0; i < target.length; i++) {
      var t = target[i];
      var c = current[i];

      if (t === THIN) {
        if (c.type !== 'sep') return this.rebuild(value);
        continue;
      }

      var digit = parseInt(t, 10);
      if (c.type !== 'digit') return this.rebuild(value);

      if (c.value !== digit) {
        c.strip.style.setProperty('--delay', immediate ? '0ms' : (i * 45) + 'ms');
        c.strip.style.setProperty('--d', digit);
        c.value = digit;
      }
    }

    this.value = value;
  };

  /* ------------------------------------------------------------------- */
  /* Контролер секції                                                    */
  /* ------------------------------------------------------------------- */

  function Gift(section, root) {
    this.section = section;
    this.root = root;
    this.stage = section.querySelector('[data-dune-gift-stage]');

    this.objectEl = section.querySelector('[data-gift-object]');
    this.cardEl = section.querySelector('[data-gift-card]');
    this.markEl = section.querySelector('[data-gift-mark]');
    this.amountBox = section.querySelector('.dune-gift__amount');
    this.amountEl = section.querySelector('[data-gift-amount]');
    this.badgeEl = section.querySelector('[data-gift-badge]');
    this.serialEl = section.querySelector('[data-gift-serial]');
    this.greetingEl = section.querySelector('[data-stage-greeting]');
    this.stageNominalEl = section.querySelector('[data-stage-nominal]');
    this.stageFormatEl = section.querySelector('[data-stage-format]');
    this.guides = {};
    toArray(section.querySelectorAll('[data-gift-guide]')).forEach(function (g) {
      this.guides[g.getAttribute('data-gift-guide')] = g;
    }, this);

    this.hintEl = root.querySelector('[data-gift-hint]');
    this.hintTextEl = root.querySelector('[data-gift-hint-text]');
    this.onlineEl = root.querySelector('[data-gift-online]');
    this.plasticNoteEl = root.querySelector('[data-gift-plastic-note]');
    this.recipientEl = root.querySelector('[data-gift-recipient]');
    this.toggleEl = root.querySelector('[data-gift-toggle]');
    this.errorEl = root.querySelector('[data-gift-error]');
    this.counterEl = root.querySelector('[data-gift-counter]');
    this.railEl = root.querySelector('[data-gift-rail]');
    this.knobEl = root.querySelector('[data-gift-knob]');
    this.levelEl = root.querySelector('[data-gift-level]');
    this.levelTextEl = root.querySelector('[data-gift-level-text]');
    this.nominalInputs = toArray(root.querySelectorAll('[data-gift-nominal]'));

    this.fields = {
      name: root.querySelector('[data-gift-field="name"]'),
      phone: root.querySelector('[data-gift-field="phone"]'),
      email: root.querySelector('[data-gift-field="email"]'),
      message: root.querySelector('[data-gift-field="message"]'),
    };
    this.giftInputs = toArray(root.querySelectorAll('[data-gift-field]'));

    this.currencySymbol = root.dataset.currencySymbol || '₴';
    this.errorName = root.dataset.errorName || '';
    this.errorContact = root.dataset.errorContact || '';
    this.recipientPrefix = root.dataset.recipientPrefix || 'Для';

    this.product = this.readProduct();
    this.odometer = this.amountEl ? new Odometer(this.amountEl) : null;

    var self = this;
    this.onChange = function (e) { self.handleChange(e); };
    this.onToggleChange = function () { self.handleToggle(); };
    this.onMessageInput = function () { self.syncMessage(); };
    this.onNameInput = function () { self.syncName(); };
    this.onAddToCartCapture = function (e) { self.handleAddToCart(e); };
    this.onPointerMove = function (e) { self.handlePointerMove(e); };
    this.onPointerLeave = function () { self.handlePointerLeave(); };
    this.applyTilt = function () { self.renderTilt(); };
    this.onResize = function () {
      clearTimeout(self.resizeTimer);
      self.resizeTimer = setTimeout(function () { self.moveKnob(true); self.placeGuides(); }, 120);
    };
    this.onCardSettled = function (e) {
      if (!e || e.target !== self.cardEl) return;
      if (e.propertyName === 'left' || e.propertyName === 'top') self.placeGuides();
    };

    this.bindForm();

    root.addEventListener('change', this.onChange);
    if (this.toggleEl) this.toggleEl.addEventListener('change', this.onToggleChange);
    if (this.fields.message) this.fields.message.addEventListener('input', this.onMessageInput);
    if (this.fields.name) this.fields.name.addEventListener('input', this.onNameInput);
    document.addEventListener('click', this.onAddToCartCapture, true);
    window.addEventListener('resize', this.onResize);
    if (this.cardEl) this.cardEl.addEventListener('transitionend', this.onCardSettled);

    this.syncFormat(true);
    this.syncRecipientState();
    this.syncNominal(true);
    this.syncMessage();
    this.syncName();
    this.setupTilt();
    this.setupReveal();

    // Повзунок і направляючі міряються по розкладці — перемірюємо після
    // шрифтів і повного завантаження.
    var settle = function () { self.moveKnob(true); self.placeGuides(); };
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(settle);
    window.addEventListener('load', settle);
    setTimeout(settle, 60);
    setTimeout(function () { if (self.stage) self.stage.classList.add('is-ready'); }, 120);
  }

  Gift.prototype.destroy = function () {
    document.removeEventListener('click', this.onAddToCartCapture, true);
    window.removeEventListener('resize', this.onResize);
  };

  /* ---------------- Дані товару і форма ---------------- */

  Gift.prototype.readProduct = function () {
    var id = this.section.getAttribute('data-section-id');
    var el = id ? document.getElementById('ProductJson-' + id) : null;
    if (!el) el = this.section.querySelector('[id^="ProductJson-"]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  };

  /**
   * Поля отримувача лежать поза формою товару — прив'язуємо їх атрибутом
   * form=. Тема серіалізує form.elements, тож прив'язане поле доїде в кошик,
   * а disabled — ні.
   */
  Gift.prototype.bindForm = function () {
    var form = this.section.querySelector('form[data-product-form]');
    if (!form) return;
    // form.id тут не годиться: у формі є <select name="id">, і іменований
    // геттер форми повертає його замість атрибута.
    var id = form.getAttribute('id');
    if (!id) {
      id = 'DuneGiftForm-' + (this.section.getAttribute('data-section-id') || 'x');
      form.setAttribute('id', id);
    }
    this.giftInputs.forEach(function (input) { input.setAttribute('form', id); });
  };

  /** Варіант за обраними радіо, з JSON товару. */
  Gift.prototype.currentVariant = function () {
    var product = this.product;
    if (!product || !product.variants || !product.options) return null;

    var names = product.options.map(function (o) {
      return (typeof o === 'string' ? o : o.name).toLowerCase();
    });
    var chosen = [];

    toArray(this.root.querySelectorAll('input[type="radio"][name^="options["]:checked')).forEach(function (input) {
      var m = /^options\[(.*)\]$/.exec(input.name);
      if (!m) return;
      var idx = names.indexOf(m[1].toLowerCase());
      if (idx >= 0) chosen[idx] = input.value;
    });

    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      var ok = true;
      for (var j = 0; j < names.length; j++) {
        if (chosen[j] !== undefined && v.options[j] !== chosen[j]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  /* ---------------- Стан ---------------- */

  Gift.prototype.checkedFormat = function () {
    return this.root.querySelector('[data-gift-format]:checked');
  };

  Gift.prototype.checkedNominal = function () {
    return this.root.querySelector('[data-gift-nominal]:checked');
  };

  Gift.prototype.isPlastic = function () {
    var checked = this.checkedFormat();
    return !!checked && checked.dataset.giftPlastic === 'true';
  };

  Gift.prototype.recipientActive = function () {
    return !this.isPlastic() && !!this.toggleEl && this.toggleEl.checked;
  };

  /* ---------------- Реакції ---------------- */

  Gift.prototype.handleChange = function (event) {
    var target = event.target;
    if (!target) return;

    if (target.hasAttribute('data-gift-format')) {
      this.syncFormat(false);
      this.syncRecipientState();
      this.hideError();
      return;
    }

    if (target.hasAttribute('data-gift-nominal')) {
      this.syncNominal(false);
    }
  };

  Gift.prototype.handleToggle = function () {
    this.syncRecipientState();
    this.hideError();
    if (this.recipientActive() && this.fields.name) this.fields.name.focus();
  };

  Gift.prototype.syncMessage = function () {
    var message = this.fields.message ? this.fields.message.value : '';
    if (this.counterEl) this.counterEl.textContent = String(message.length);
    if (this.greetingEl) {
      var text = message.trim();
      if (text.length > 72) text = text.slice(0, 72) + '…';
      this.greetingEl.textContent = text || this.greetingEl.dataset.default || '';
    }
  };

  Gift.prototype.syncName = function () {
    if (!this.serialEl) return;
    var name = this.fields.name && this.recipientActive() ? this.fields.name.value.trim() : '';
    this.serialEl.textContent = name ? this.recipientPrefix + ' · ' + name : (this.serialEl.dataset.default || '');
    this.serialEl.classList.toggle('is-named', !!name);
  };

  /* ---------------- Номінал ---------------- */

  Gift.prototype.syncNominal = function (immediate) {
    var checked = this.checkedNominal();
    if (!checked) return;

    var index = this.nominalInputs.indexOf(checked);
    var count = this.nominalInputs.length;

    var variant = this.currentVariant();
    var amount = variant && typeof variant.price === 'number'
      ? Math.round(variant.price / 100)
      : parseInt(String(checked.value).replace(/\D/g, ''), 10);
    if (!isNaN(amount)) this.setAmount(amount, immediate);

    this.moveKnob(immediate);

    if (this.levelEl && count > 1 && index >= 0) {
      this.levelEl.style.setProperty('--level', ((index / (count - 1)) * 100).toFixed(2) + '%');
    }
    if (this.levelTextEl && index >= 0) {
      this.levelTextEl.textContent = (index + 1) + ' / ' + count;
    }
  };

  Gift.prototype.setAmount = function (amount, immediate) {
    if (this.odometer) this.odometer.set(amount, immediate);
    this.flipText(this.stageNominalEl, groupThousands(amount) + THIN + this.currencySymbol, immediate);

    if (!immediate && this.stage && !REDUCED) {
      var stage = this.stage;
      stage.classList.add('is-flash');
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(function () { stage.classList.remove('is-flash'); }, 700);
    }
  };

  Gift.prototype.moveKnob = function (immediate) {
    if (!this.railEl || !this.knobEl) return;

    var checked = this.checkedNominal();
    var label = checked ? checked.nextElementSibling : null;
    if (!label) return;

    var rail = this.railEl.getBoundingClientRect();
    var box = label.getBoundingClientRect();
    if (!box.width) return;

    var knob = this.knobEl;
    if (immediate) knob.classList.add('no-anim');

    knob.style.left = (box.left - rail.left) + 'px';
    knob.style.top = (box.top - rail.top) + 'px';
    knob.style.width = box.width + 'px';
    knob.style.height = box.height + 'px';

    if (immediate) {
      void knob.offsetWidth;
      knob.classList.remove('no-anim');
    }
  };

  /* ---------------- Направляючі ---------------- */

  /**
   * Дві горизонтальні лінії по верху й низу суми на картці (цифри стоять між
   * направляючими, як літери в знаку DUNE), дві вертикальні — по краях
   * картки. Міряємо offset-ланцюжком, він не залежить від 3D-повороту.
   */
  Gift.prototype.placeGuides = function () {
    var markEl = this.amountBox || this.markEl;
    if (!this.stage || !this.cardEl || !markEl) return;

    var stage = this.stage;
    var offsetTo = function (el) {
      var x = 0, y = 0, node = el;
      while (node && node !== stage) {
        x += node.offsetLeft;
        y += node.offsetTop;
        node = node.offsetParent;
      }
      return { x: x, y: y };
    };

    var card = offsetTo(this.cardEl);
    var mark = offsetTo(markEl);
    var markH = markEl.offsetHeight;

    // Коробка рядка вища за прописні літери: лінії ставимо на оптичний край.
    var capTop = mark.y + markH * 0.14;
    var capBottom = mark.y + markH * 0.86;

    stage.style.setProperty('--g-top', capTop.toFixed(1) + 'px');
    stage.style.setProperty('--g-bottom', capBottom.toFixed(1) + 'px');
    stage.style.setProperty('--g-left', card.x.toFixed(1) + 'px');
    stage.style.setProperty('--g-right', (card.x + this.cardEl.offsetWidth).toFixed(1) + 'px');
  };

  /* ---------------- Формат ---------------- */

  Gift.prototype.syncFormat = function (immediate) {
    var checked = this.checkedFormat();
    var plastic = this.isPlastic();

    if (checked) {
      if (this.badgeEl) this.badgeEl.textContent = checked.value;
      this.flipText(this.stageFormatEl, checked.value, immediate);
      this.swapHint(checked.dataset.hintText || '', immediate);
    }

    if (this.stage) this.stage.setAttribute('data-format', plastic ? 'plastic' : 'online');

    if (this.onlineEl) this.onlineEl.hidden = plastic;
    if (this.plasticNoteEl) this.plasticNoteEl.hidden = !plastic;

    if (immediate) this.placeGuides();
  };

  Gift.prototype.swapHint = function (text, immediate) {
    if (!this.hintEl || !this.hintTextEl) return;

    if (immediate || this.hintTextEl.textContent === text) {
      this.hintTextEl.textContent = text;
      return;
    }

    var self = this;
    this.hintEl.classList.add('is-swapping');
    clearTimeout(this.hintTimer);
    this.hintTimer = setTimeout(function () {
      self.hintTextEl.textContent = text;
      self.hintEl.classList.remove('is-swapping');
    }, 220);
  };

  Gift.prototype.flipText = function (el, text, immediate) {
    if (!el) return;

    if (immediate || el.textContent === text) {
      el.textContent = text;
      return;
    }

    el.classList.add('is-flip');
    setTimeout(function () {
      el.textContent = text;
      el.classList.remove('is-flip');
    }, 200);
  };

  /* ---------------- Кому ---------------- */

  Gift.prototype.syncRecipientState = function () {
    var active = this.recipientActive();

    if (this.recipientEl) {
      this.recipientEl.classList.toggle('is-open', active);
      if (active) {
        this.recipientEl.removeAttribute('inert');
        this.recipientEl.setAttribute('aria-hidden', 'false');
      } else {
        this.recipientEl.setAttribute('inert', '');
        this.recipientEl.setAttribute('aria-hidden', 'true');
      }
    }

    // disabled-поле тема в кошик не кладе — так порожнє й вимкнене не доїжджає.
    this.giftInputs.forEach(function (input) { input.disabled = !active; });

    this.syncName();
  };

  /* ---------------- Нахил ---------------- */

  Gift.prototype.setupTilt = function () {
    if (REDUCED || !this.stage || !this.objectEl) return;
    this.stage.addEventListener('pointermove', this.onPointerMove);
    this.stage.addEventListener('pointerleave', this.onPointerLeave);
  };

  Gift.prototype.handlePointerMove = function (event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.pointer = { x: event.clientX, y: event.clientY };
    if (!this.tiltFrame) this.tiltFrame = window.requestAnimationFrame(this.applyTilt);
  };

  Gift.prototype.renderTilt = function () {
    this.tiltFrame = null;
    if (!this.pointer || !this.stage) return;

    var rect = this.stage.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var x = Math.max(-1, Math.min(1, ((this.pointer.x - rect.left) / rect.width) * 2 - 1));
    var y = Math.max(-1, Math.min(1, ((this.pointer.y - rect.top) / rect.height) * 2 - 1));

    this.objectEl.classList.add('is-tilting');
    this.objectEl.style.setProperty('--rx', (-y * 6).toFixed(2) + 'deg');
    this.objectEl.style.setProperty('--ry', (x * 8).toFixed(2) + 'deg');
    if (this.cardEl) this.cardEl.style.setProperty('--sheen', (x * 70).toFixed(1) + '%');
  };

  Gift.prototype.handlePointerLeave = function () {
    this.pointer = null;
    if (!this.objectEl) return;
    this.objectEl.classList.remove('is-tilting');
    this.objectEl.style.setProperty('--rx', '0deg');
    this.objectEl.style.setProperty('--ry', '0deg');
    if (this.cardEl) this.cardEl.style.removeProperty('--sheen');
  };

  /* ---------------- Поява при прокрутці ---------------- */

  Gift.prototype.setupReveal = function () {
    var items = toArray(this.section.querySelectorAll('.dune-gift__reveal'));
    if (!items.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      items.forEach(function (item) { item.classList.add('is-in'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    items.forEach(function (item) { observer.observe(item); });
  };

  /* ---------------- Перевірка перед кошиком ---------------- */

  Gift.prototype.validate = function () {
    if (!this.recipientActive()) return null;

    var name = this.fields.name ? this.fields.name.value.trim() : '';
    var phone = this.fields.phone ? this.fields.phone.value.trim() : '';
    var email = this.fields.email ? this.fields.email.value.trim() : '';

    if (!name) return { message: this.errorName, field: this.fields.name };
    if (!phone && !email) return { message: this.errorContact, field: this.fields.phone };
    return null;
  };

  Gift.prototype.showError = function (problem) {
    if (this.errorEl) {
      this.errorEl.textContent = problem.message;
      this.errorEl.hidden = false;

      var box = this.errorEl.getBoundingClientRect();
      var offscreen = box.top < 0 || box.bottom > (window.innerHeight || 0);
      if (offscreen && typeof this.errorEl.scrollIntoView === 'function') {
        this.errorEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }

    if (problem.field) {
      problem.field.classList.add('dune-gift__input--invalid');
      problem.field.focus({ preventScroll: true });
    }
  };

  Gift.prototype.hideError = function () {
    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
    this.giftInputs.forEach(function (input) { input.classList.remove('dune-gift__input--invalid'); });
  };

  /**
   * Порожні поля вимикаємо перед самою відправкою, щоб у кошик не поїхали
   * порожні properties[...]. Вмикаємо назад після того, як тема зібрала
   * форму (вона робить це синхронно у цьому ж кліку).
   */
  Gift.prototype.stripEmptyForSubmit = function () {
    var restore = [];

    this.giftInputs.forEach(function (input) {
      if (input.disabled) return;
      input.value = input.value.trim();
      if (input.value === '') {
        input.disabled = true;
        restore.push(input);
      }
    });

    if (!restore.length) return;
    setTimeout(function () {
      restore.forEach(function (input) { input.disabled = false; });
    }, 0);
  };

  Gift.prototype.handleAddToCart = function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var button = target.closest('[data-add-to-cart]');
    if (!button || !this.section.contains(button)) return;

    var problem = this.validate();
    if (problem) {
      event.preventDefault();
      event.stopPropagation();
      this.showError(problem);
      return;
    }

    this.hideError();
    this.stripEmptyForSubmit();
  };

  /* ------------------------------------------------------------------- */
  /* Запуск                                                              */
  /* ------------------------------------------------------------------- */

  function init(scope) {
    toArray((scope || document).querySelectorAll('[data-dune-gift]')).forEach(function (root) {
      if (root.duneGift) return;
      var section = root.closest('[data-section-type="product"]');
      if (!section) return;
      root.duneGift = new Gift(section, root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Редактор теми перемальовує секцію — піднімаємо контролер заново.
  document.addEventListener('shopify:section:load', function (e) {
    var old = e.target && e.target.querySelector('[data-dune-gift]');
    if (old && old.duneGift) { old.duneGift.destroy(); old.duneGift = null; }
    init(e.target);
  });
})();
